import React, { useEffect, useState } from "react";
import {
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
} from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { useQuery, useMutation } from "react-query";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { DatePicker } from "@mui/x-date-pickers";
import CommonDialog from "../CommonDialog";
import { CustomTextField2 } from "assets/styles/globalStyledComponent";
import { selectActiveSession } from "app/modules/schools/selectors";
import {
  getAllLibraryBooks,
  issueBookToUser,
  getLibraryBookByName,
} from "app/services/schoolReportingServices/library.service";
import StudentSearchDialogDashboard from "app/components/common/Dialogs/StudentSearchDialogDashboard";
import StaffSeachDialogDashboard2 from "app/components/common/StaffSeachDialogDashboard2";
import SearchedCard from "app/modules/library/components/SearchedCard";
import SearchedBookCard from "app/modules/library/components/SearchedBookCard";
import TextFieldLabel from "app/components/common/TextFieldLabel";
import SearchInInputField from "app/components/common/SearchInInputField";
import { LoadingButton } from "@mui/lab";

const IssueBookDialog = ({
  open = false,
  onClose = () => {},
  refetch = () => {},
}) => {
  const selectedSession = useSelector(selectActiveSession);
  const [borrowerType, setBorrowerType] = useState("Student");
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [selectedBook, setSelectedBook] = useState(null);
  const dt = new Date();

  // --- Handlers ---
  const handleClose = () => {
    setSelectedBook(null);
    setSelectedPerson(null);
    setBorrowerType("Student");
    formik.resetForm();
    onClose();
  };

  // Fetch all books for dropdown
  const { data: allBooksData, isLoading: allBooksLoading } = useQuery(
    ["getAllLibraryBooks", selectedSession],
    () =>
      getAllLibraryBooks({
        session: selectedSession,
      }),
    {
      enabled: open,
      staleTime: 1000,
    }
  );

  // Formik setup
  const formik = useFormik({
    initialValues: {
      libraryBookDocId: "",
      borrowerType: "Student",
      borrowerDocId: "",
      issuedDate: "",
      dueDate: "",
    },
    enableReinitialize: true,
    validationSchema: yup.object({
      libraryBookDocId: yup.string().required("Book is required"),
      borrowerType: yup.string().oneOf(["Student", "Staff"]).required(),
      borrowerDocId: yup.string().required("Name is required"),
      issuedDate: yup.date().required("Issue date is required"),
      dueDate: yup
        .date()
        .required("Due date is required")
        .min(yup.ref("issuedDate"), "Due date cannot be before issue date"),
    }),
    onSubmit: (values, { resetForm }) => {
      // Format date as YYYY-MM-DD in local timezone
      const formatLocalDate = (date) => {
        if (!date) return "";
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      };
      issueBookToUserMutate({
        ...values,
        issuedDate: formatLocalDate(values.issuedDate),
        dueDate: formatLocalDate(values.dueDate),
      });
    },
  });

  // Issue book mutation
  const { mutate: issueBookToUserMutate, isLoading: issueLoading } =
    useMutation(issueBookToUser, {
      onSuccess: (res) => {
        toast.success(res?.data?.message || "Book issued successfully");
        formik.resetForm();
        setSelectedPerson(null); // <-- clear selected person after issue
        onClose();
        refetch();
      },
      onError: (err) => {
        toast.error(err?.response?.data?.message || "Failed to issue book");
      },
    });

  // Handle borrower type change
  useEffect(() => {
    formik.setFieldValue("borrowerType", borrowerType);
    formik.setFieldValue("borrowerDocId", "");
    setSelectedPerson(null);
  }, [borrowerType]);

  useEffect(() => {
    if (!open) {
      setSelectedBook(null);
      setSelectedPerson(null);
      formik.resetForm();
    }
    // eslint-disable-next-line
  }, [open]);

  return (
    <CommonDialog
      open={open}
      onClose={handleClose}
      title="Issue Book"
      minWidth="500px"
      fullWidth
    >
      <form onSubmit={formik.handleSubmit}>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextFieldLabel title="Select Book Name" />
              <SearchInInputField
                placeholder="Type to search books..."
                fetchOptions={async (searchText) => {
                  if (!searchText) return [];
                  const res = await getLibraryBookByName({
                    session: selectedSession,
                    bookName: searchText,
                  });
                  return res?.data?.list || [];
                }}
                onSelect={(book) => {
                  formik.setFieldValue("libraryBookDocId", book._id);
                  setSelectedBook(book);
                }}
                value={
                  formik.values.libraryBookDocId
                    ? formik.values.libraryBookDocId
                    : ""
                }
                getOptionLabel={(option) => option.title}
                disabled={issueLoading}
                minSearchLength={2}
              />
              {selectedBook && (
                <Box mt={2}>
                  <SearchedBookCard book={selectedBook} />
                </Box>
              )}
              {formik.touched.libraryBookDocId &&
                formik.errors.libraryBookDocId && (
                  <Typography color="error" fontSize={12}>
                    {formik.errors.libraryBookDocId}
                  </Typography>
                )}
            </Grid>

            <Grid item xs={12}>
              <TextFieldLabel title="Select Borrower Type" />
              <FormControl component="fieldset">
                <RadioGroup
                  row
                  name="borrowerType"
                  value={borrowerType}
                  onChange={(e) => setBorrowerType(e.target.value)}
                >
                  <FormControlLabel
                    value="Student"
                    control={<Radio color="secondary" />}
                    label="Student"
                  />
                  <FormControlLabel
                    value="Staff"
                    control={<Radio color="secondary" />}
                    label="Staff"
                  />
                </RadioGroup>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              {borrowerType === "Student" ? (
                <Box>
                  <TextFieldLabel title="Student Name" />
                  <StudentSearchDialogDashboard
                    value="Student Name"
                    radio="activeSession"
                    isNavigateToProfile={false}
                    returnResponse={(item) => {
                      const studentId = item?.studentDocId;
                      formik.setFieldValue("borrowerDocId", studentId);
                      setSelectedPerson(item);
                    }}
                  />
                  {selectedPerson && borrowerType === "Student" && (
                    <Box mt={2}>
                      <SearchedCard item={selectedPerson} />
                    </Box>
                  )}
                  {formik.touched.borrowerDocId &&
                    formik.errors.borrowerDocId && (
                      <Typography color="error" fontSize={12}>
                        {formik.errors.borrowerDocId}
                      </Typography>
                    )}
                </Box>
              ) : (
                <Box>
                  <TextFieldLabel title="Staff Name" />
                  <StaffSeachDialogDashboard2
                    value="Staff Name"
                    isNavigateToProfile={false}
                    returnResponse={(item) => {
                      const staffId = item?.staffDocId || item?._id;
                      formik.setFieldValue("borrowerDocId", staffId);
                      setSelectedPerson(item);
                      if (!staffId) {
                        console.warn(
                          "Staff selection missing staffDocId/_id",
                          item
                        );
                      }
                    }}
                  />
                  {selectedPerson && borrowerType === "Staff" && (
                    <Box mt={2}>
                      <SearchedCard item={selectedPerson} />
                    </Box>
                  )}
                  {formik.touched.borrowerDocId &&
                    formik.errors.borrowerDocId && (
                      <Typography color="error" fontSize={12}>
                        {formik.errors.borrowerDocId}
                      </Typography>
                    )}
                </Box>
              )}
            </Grid>

            <Grid item xs={6}>
              <TextFieldLabel title="Issue Date" />
              <DatePicker
                minDate={new Date()}
                inputFormat="DD-MM-YYYY"
                value={formik.values.issuedDate}
                onChange={(date) => formik.setFieldValue("issuedDate", date)}
                renderInput={(params) => (
                  <CustomTextField2
                    {...params}
                    fullWidth
                    error={
                      formik.touched.issuedDate &&
                      Boolean(formik.errors.issuedDate)
                    }
                    helperText={
                      formik.touched.issuedDate && formik.errors.issuedDate
                    }
                  />
                )}
              />
            </Grid>

            <Grid item xs={6}>
              <TextFieldLabel title="Due Date" />
              <DatePicker
                minDate={formik.values.issuedDate}
                inputFormat="DD-MM-YYYY"
                value={formik.values.dueDate}
                onChange={(date) => formik.setFieldValue("dueDate", date)}
                renderInput={(params) => (
                  <CustomTextField2
                    {...params}
                    fullWidth
                    error={
                      formik.touched.dueDate && Boolean(formik.errors.dueDate)
                    }
                    helperText={formik.touched.dueDate && formik.errors.dueDate}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="error" variant="outlined">
            Cancel
          </Button>
          <LoadingButton
            type="submit"
            color="secondary"
            variant="contained"
            loading={issueLoading}
          >
            Issue Book
          </LoadingButton>
        </DialogActions>
      </form>
    </CommonDialog>
  );
};

export default IssueBookDialog;
